/**
 * Analytics lean baseado em Supabase (tabela `events`).
 *
 * Filosofia: pra escala atual (27 testers, dezenas de check-ins/dia), não vale
 * dependência de PostHog/GA — a tabela `events` no Supabase é mais que
 * suficiente e fica tudo num só lugar.
 *
 * Características:
 *  - Best-effort: erros silenciosos (não bloqueiam fluxo do app)
 *  - Buffer + flush em batch (até 5 eventos ou 3s) pra reduzir requests
 *  - User id opcional (eventos pré-login também são logados)
 *  - Eventos têm `event_name` (string canônica) + `properties` (jsonb)
 *
 * Eventos comuns:
 *  - `login_succeeded` / `login_failed`
 *  - `register_started` / `register_succeeded` / `register_failed`
 *  - `partner_viewed` (sampled)
 *  - `checkin_completed` (com partner_id, value, discount)
 *  - `theme_equipped` / `frame_equipped`
 *  - `app_installed_pwa`
 *
 * Pra adicionar evento novo: chama `trackEvent('nome_do_evento', { ... })`.
 */
import { supabase } from '../supabase';

interface QueuedEvent {
  event_name: string;
  properties: Record<string, unknown>;
  occurred_at: string;
}

const queue: QueuedEvent[] = [];
const FLUSH_INTERVAL_MS = 3_000;
const MAX_QUEUE_SIZE = 5;
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let currentUserId: string | null = null;

async function flush() {
  if (queue.length === 0) return;
  const batch = queue.splice(0, queue.length);
  try {
    await supabase.from('events').insert(
      batch.map((e) => ({
        event_name: e.event_name,
        properties: e.properties,
        occurred_at: e.occurred_at,
        user_id: currentUserId,
      })),
    );
  } catch (err) {
    // Silencioso — analytics não pode quebrar UX
    console.warn('[analytics] flush falhou', err);
  }
}

function scheduleFlush() {
  if (flushTimer) return;
  flushTimer = setTimeout(() => {
    flushTimer = null;
    void flush();
  }, FLUSH_INTERVAL_MS);
}

/**
 * Registra um evento. Não bloqueia — vai pra um buffer e é enviado em batch.
 */
export function trackEvent(name: string, properties: Record<string, unknown> = {}): void {
  queue.push({
    event_name: name,
    properties,
    occurred_at: new Date().toISOString(),
  });
  if (queue.length >= MAX_QUEUE_SIZE) {
    void flush();
    if (flushTimer) {
      clearTimeout(flushTimer);
      flushTimer = null;
    }
  } else {
    scheduleFlush();
  }
}

/**
 * Set o user id atual. Chamar quando o usuário logar/deslogar.
 */
export function setAnalyticsUser(userId: string | null): void {
  currentUserId = userId;
}

/**
 * Helper de "sampling" — útil pra eventos de alto volume (ex.: partner_viewed)
 * que não precisam ser 100% capturados pra dar sinal estatístico.
 *
 * Uso: `if (sampled(0.2)) trackEvent('partner_viewed', { ... })` — 20% das views.
 */
export function sampled(rate: number): boolean {
  return Math.random() < rate;
}

// Flush quando o usuário sai (best-effort) — usa sendBeacon se disponível pra
// garantir entrega. Aqui só dispara o flush normal — o Supabase JS client não
// suporta sendBeacon nativo. Em prática, com flush a cada 3s, é raro perder
// eventos no fechamento.
if (typeof window !== 'undefined') {
  window.addEventListener('pagehide', () => {
    void flush();
  });
  window.addEventListener('beforeunload', () => {
    void flush();
  });
}
